//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { FeatureNames } from '@bvengineering/curalate-features';

import { CampaignsStateFactory } from '@campaigns/routes/stateFactory';

import {
  componentNameToTemplate,
  hasFeature,
} from 'app/initialization/routing/stateHelpers';

export const samplingStates = {
  'campaigns.sampling': CampaignsStateFactory({
    url: '/sampling?:chartMetric&:startDate&:endDate',
    views: {
      main: {
        template: componentNameToTemplate('campaignsOverviewPage'),
      },
    },
    data: {
      title: 'Campaigns: Bazaarvoice Sampling',
    },
  }),

  'campaigns.sampling-campaign-request-details': CampaignsStateFactory({
    url: '/sampling/campaign-request/:campaignId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Request Details',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingRequestDetailsPage',
        ),
      },
    },
  }),

  'campaigns.sampling-campaign-request-details-quote': CampaignsStateFactory({
    url: '/sampling/campaign-request/:campaignId/quote',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Request Quote',
      hasAccess: () => hasFeature(FeatureNames.CastaCrfQuote), // TODO: PD-304383 - Remove feature flag check once CastaCrfQuote is released
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingRequestQuoteDetailsPage',
        ),
      },
    },
  }),

  'campaigns.sampling-campaign-request-details-edit': CampaignsStateFactory({
    url: '/sampling/campaign-request/:campaignId/edit',
    redirectTo: 'campaigns.sampling-campaign-request-details-edit-products',
  }),

  'campaigns.sampling-campaign-request-details-edit-products':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/products',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign-request-details-edit-audience':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/audience',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign-request-details-edit-tasks':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/tasks',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign-request-details-edit-packaging':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/packaging',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign-request-details-edit-fulfillment':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/fulfillment',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign-request-details-edit-purchase-options':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/purchase-options',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign-request-details-edit-summary':
    CampaignsStateFactory({
      url: '/sampling/campaign-request/:campaignId/edit/summary',

      data: {
        title: 'Campaigns: Bazaarvoice Sampling Request Edit',
      },
      views: {
        main: {
          template: componentNameToTemplate('campaignsSamplingRequestEditPage'),
        },
      },
    }),

  'campaigns.sampling-campaign': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId',
    redirectTo: 'campaigns.sampling-campaign-details-overview',
  }),

  // Route for the Overview tab
  'campaigns.sampling-campaign-details-overview': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/overview?:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Overview',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Route for the Request tab
  'campaigns.sampling-campaign-details-request': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/request?:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Request',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Route for the Social tab
  'campaigns.sampling-campaign-details-social': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/social?:view&:fromReportId',
    redirectTo: 'campaigns.sampling-campaign-details-social-media',
  }),

  // Route for the Social tab with Media sub-tab
  'campaigns.sampling-campaign-details-social-media': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/social/media?:view&:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Social - Media',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Route for the Social tab with Syndication sub-tab
  'campaigns.sampling-campaign-details-social-syndication':
    CampaignsStateFactory({
      url: '/sampling/campaign/:campaignId/social/syndication?:fromReportId',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Campaign Social - Syndication',
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingCampaignDetailsPage',
          ),
        },
      },
    }),

  // Route for the Review tab with Products sub-tab
  'campaigns.sampling-campaign-details-review-products': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/review/products?:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Review - Products',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Route for the Review tab with Reviews sub-tab
  'campaigns.sampling-campaign-details-review-reviews': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/review/reviews?:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Review - Reviews',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Route for the Review tab with Syndication sub-tab
  'campaigns.sampling-campaign-details-review-syndication':
    CampaignsStateFactory({
      url: '/sampling/campaign/:campaignId/review/syndication?:fromReportId',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Campaign Review - Syndication',
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingCampaignDetailsPage',
          ),
        },
      },
    }),

  // Route for the Review tab with Media sub-tab
  'campaigns.sampling-campaign-details-review-media': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/review/media?:view&:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Review - Media',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Route for the Post Survey tab
  'campaigns.sampling-campaign-details-post-survey': CampaignsStateFactory({
    url: '/sampling/campaign/:campaignId/post-survey?:fromReportId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Campaign Post Survey',
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingCampaignDetailsPage',
        ),
      },
    },
  }),

  // Prepaid Campaign Flow Routes
  'campaigns.sampling-prepaid-campaign-request-details': CampaignsStateFactory({
    url: '/prepaid/campaign-request/:campaignId',
    data: {
      title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Details',
      hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
    },
    views: {
      main: {
        template: componentNameToTemplate(
          'campaignsSamplingPrepaidRequestDetailsPage',
        ),
      },
    },
  }),

  'campaigns.sampling-prepaid-campaign-request-details-edit':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit',
      redirectTo:
        'campaigns.sampling-prepaid-campaign-request-details-edit-products',
    }),

  'campaigns.sampling-prepaid-campaign-request-details-edit-products':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit/products',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Edit',
        hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingPrepaidRequestEditPage',
          ),
        },
      },
    }),

  'campaigns.sampling-prepaid-campaign-request-details-edit-audience':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit/audience',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Edit',
        hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingPrepaidRequestEditPage',
          ),
        },
      },
    }),

  'campaigns.sampling-prepaid-campaign-request-details-edit-tasks':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit/tasks',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Edit',
        hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingPrepaidRequestEditPage',
          ),
        },
      },
    }),

  'campaigns.sampling-prepaid-campaign-request-details-edit-packaging':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit/packaging',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Edit',
        hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingPrepaidRequestEditPage',
          ),
        },
      },
    }),

  'campaigns.sampling-prepaid-campaign-request-details-edit-fulfillment':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit/fulfillment',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Edit',
        hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingPrepaidRequestEditPage',
          ),
        },
      },
    }),

  'campaigns.sampling-prepaid-campaign-request-details-edit-summary':
    CampaignsStateFactory({
      url: '/prepaid/campaign-request/:campaignId/edit/summary',
      data: {
        title: 'Campaigns: Bazaarvoice Sampling Prepaid Request Edit',
        hasAccess: () => hasFeature(FeatureNames.SamplingPrepaidCampaigns),
      },
      views: {
        main: {
          template: componentNameToTemplate(
            'campaignsSamplingPrepaidRequestEditPage',
          ),
        },
      },
    }),
};
